import * as React from 'react';
import { useContext, useState } from 'react';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Box from '@mui/material/Box';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { PlantingPlannerDataGrid } from '../PlantingPlannerDataGrid';
import { FrostDatesContext, PlantingLocationContext, PlantsContext } from '../AppContext';
import { FrostDates, PlantingLocation, SeasonPlantingPlan } from '../Types';
import { Button, FormControl, Typography, styled } from '@mui/material';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import Grid from '@mui/material/Grid';
import { grey } from '@mui/material/colors';
import PrintIcon from '@mui/icons-material/Print';
import Select from '@mui/material/Select';
import OutlinedInput from '@mui/material/OutlinedInput';
import MenuItem from '@mui/material/MenuItem';
import { SelectChangeEvent } from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import Chip from '@mui/material/Chip';
import { useTheme } from '@mui/material/styles';
interface TabPanelProps {
    children?: React.ReactNode;
    dir?: string;
    index: number;
    value: number;
}

function a11yProps(plan: SeasonPlantingPlan) {
    const name = plan == SeasonPlantingPlan.Spring ? 'spring-planting' : 'fall-planting';
    return {
        id: `full-width-tab-${name}`,
        'aria-controls': `full-width-tabpanel-${name}`,
    };
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}
function SeasonTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <Box
            role="tabpanel"
            hidden={value !== index}
            id={`season-tabpanel-${index}`}
            aria-labelledby={`season-tab-${index}`}
            sx={{ minHeight: '300px', width: '100%', breakAfter: 'avoid' }}
            {...other}
        >
            {value === index && (
                <>{children}</>
            )}
        </Box>
    );
}
const SeasonTab = styled(Tab)(({ theme }) => {
    return {
        [theme.breakpoints.down('md')]: {
            maxWidth: '80vw'
        },
        minWidth: '50%',
        // maxWidth: '50%',

        textTransform: 'none',
        borderRadius: '6px 6px 0 0',
        borderTop: `1px solid ${grey[500]}`,
        borderLeft: `1px solid ${grey[500]}`,
        borderRight: `1px solid ${grey[500]}`,
        opacity: .7,
        '&.Mui-selected': {
            borderTop: `2px solid ${theme.palette.primary.main}`,
            borderLeft: `2px solid ${theme.palette.primary.main}`,
            borderRight: `2px solid ${theme.palette.primary.main}`,
            opacity: 1
        },
    };

});
const TabLabelContainer = styled(Box)(({ theme }) => {
    return {
        [theme.breakpoints.down('md')]: {
            flexDirection: 'column',
        },
        display: 'flex',
        flexDirection: 'row',
        justifyItems: 'center',
        alignItems: 'center',
        width: '100%',
        flexWrap: 'wrap'
    }
});

function SeasonTabLabel({ season }: { season: SeasonPlantingPlan }) {
    const { frostDates } = useContext(FrostDatesContext);
    const theme = useTheme();
    const frostDate = season === SeasonPlantingPlan.Spring ? frostDates?.lastDate.day.format('MMM DD') : frostDates?.firstDate.day.format('MMM DD');
    return <TabLabelContainer className='tabsBox'>
        <Typography letterSpacing='4px' fontWeight='bold' fontSize='1.6em' sx={{
            [theme.breakpoints.down('md')]: { fontSize: '1.3em', fontWeight: 'bold' },
            marginRight: '1em', height: 'fit-content'
        }}>{season === SeasonPlantingPlan.Spring ? 'SPRING' : 'FALL'}&nbsp;PLANTING</Typography>
        <Box sx={{ display: 'flex', flexGrow: 1, textAlign: 'right', flexDirection: 'row', justifyItems: 'center', alignItems: 'center', flexWrap: 'nowrap' }}>
            <Typography
                fontSize='1.2em' fontWeight='bold' fontStyle='italic' textAlign='right'
                component='span'
                sx={{
                    [theme.breakpoints.down('md')]: {
                        textAlign: 'left',
                        width: 'min-content'
                    },
                    marginRight: '1em',
                    height: 'fit-content',
                    
                }}>Average&nbsp;Frost Probability&nbsp;{frostDates?.firstDate.probability}%</Typography>
            <CalendarMonthIcon fontSize='large' sx={{ height: '30px', wdith: '30px' }} />&nbsp;
            <Typography fontWeight='bold' fontSize='1.5em' noWrap sx={{ marginLeft: '.5em', height: 'fit-content', flexWrap: 'wrap' }}>{frostDate}</Typography>
        </Box>
    </TabLabelContainer>
}
export type SeasonalPlantingPlannerTabsProps = {
    plantingLocation: PlantingLocation | null;
    frostDates: FrostDates | null;
}
const TableHeader = styled(Typography)(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    border: `1px solid ${theme.palette.primary.main}`,
    breakAfter: 'avoid',
    color: theme.palette.primary.contrastText,
    padding: theme.spacing(1),
    fontWeight: 'bold',
    fontSize: '2em'
}));
const FilterSelectControl = styled(FormControl)(({ theme }) => {
    return {
        [theme.breakpoints.down('md')]: {
            flexGrow: 1
        },
        width: 300,
        margin: theme.spacing(1),
    };
});
function getCategoryStyles(name: string, personName: readonly string[]) {
    return {
        fontWeight:
            personName.indexOf(name) === -1
                ? 'normal'
                : 'bold',
    };
}
const PlantingLocationTabs = styled(Tabs)(({ theme }) => {
    return {
        height: '100%',
        '&.MuiTabs-root': {
            '& .MuiTabs-scroller': {
                display: 'flex',
                alignItems: 'end',
                flexGrow: 1,
                '& .MuiTabs-flexContainer': {
                    width: '100%'
                }
            }
        }
    };
});
export default function SeasonalPlantingPlannerTabs() {
    const [season, setSeason] = useState(SeasonPlantingPlan.Spring);
    const { plantingLocation } = useContext(PlantingLocationContext);
    const handleSeasonChange = (_: React.SyntheticEvent, newValue: SeasonPlantingPlan) => {
        setSeason(newValue);
    };
    const { plantCategories, categoryDictionary } = useContext(PlantsContext);

    const tabs = plantingLocation ? <PlantingLocationTabs value={season} onChange={handleSeasonChange} aria-label="Seasonal planting planner selection.">
        <SeasonTab label={<SeasonTabLabel season={SeasonPlantingPlan.Spring} />} {...a11yProps(SeasonPlantingPlan.Spring)} />
        <SeasonTab label={<SeasonTabLabel season={SeasonPlantingPlan.Fall} />} {...a11yProps(SeasonPlantingPlan.Fall)} />
    </PlantingLocationTabs> : <Typography sx={{
        opacity: .5,
        width: '100%',
        flexGrow: 1
    }} textAlign='center' variant="h5" > Please provide your zip code above to load crop information.</Typography>;

    const print = plantingLocation ?
        <Button variant='outlined' size='large' endIcon={<PrintIcon />} onClick={() => {
            setTimeout(() => window.print(), 500);
        }}>Print</Button> : '';
    const [categoryNames, setCategoryName] = React.useState<string[]>([]);
    const handleCategoryChange = (event: SelectChangeEvent<typeof categoryNames>) => {
        const {
            target: { value },
        } = event;
        setCategoryName(
            // On autofill we get a stringified value.
            typeof value === 'string' ? value.split(',') : value,
        );
    };
    const filter = plantingLocation ? <FilterSelectControl sx={{ m: 1, width: 300 }}>
        <InputLabel id="crop-filter-select-label">Filter</InputLabel>
        <Select
            labelId="crop-filter-select-label"
            id="crop-filter-select"
            multiple
            value={categoryNames}
            onChange={handleCategoryChange}
            input={<OutlinedInput color='primary' label="Chip" />}
            renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                        <Chip key={value} label={value} />
                    ))}
                </Box>
            )}
        >
            {plantCategories.map((name) => (
                <MenuItem
                    key={name}
                    value={name}
                    style={getCategoryStyles(name, categoryNames)}
                >
                    {name}
                </MenuItem>
            ))}
        </Select>
    </FilterSelectControl> : '';
    const panels = plantingLocation ? <>
        <SeasonTabPanel value={season} index={SeasonPlantingPlan.Spring}>
            {(() => {
                const categoryGrids = [] as React.ReactNode[]
                for (const [category, plants] of Object.entries(categoryDictionary)) {
                    if (categoryNames.includes(category) || categoryNames.length === 0)
                    {
                        categoryGrids.push(<TableHeader key={`${category}-title`}>{category}</TableHeader>)
                        categoryGrids.push(<PlantingPlannerDataGrid key={category} season={SeasonPlantingPlan.Spring} rows={plants} />);
                    }
                }
                return <>
                    {categoryGrids}
                </>
            })()}
        </SeasonTabPanel>
        <SeasonTabPanel value={season} index={SeasonPlantingPlan.Fall}>
            {(() => {
                const categoryGrids = [] as React.ReactNode[]
                for (const [category, plants] of Object.entries(categoryDictionary)) {
                    if (categoryNames.includes(category) || categoryNames.length === 0) {
                        categoryGrids.push(<TableHeader key={`${category}-title`}>{category}</TableHeader>)
                        categoryGrids.push(<PlantingPlannerDataGrid key={category} season={SeasonPlantingPlan.Fall} rows={plants} />);
                    }
                }
                return <>
                    {categoryGrids}
                </>
            })()}
        </SeasonTabPanel>
    </> : < ThermostatIcon sx={{
        width: '100%',
        minHeight: '300px',
        marginX: 'auto',
        opacity: .7
    }} />
    return (
        <Grid container px={{xs: 1, sm: 2, md: 3}} paddingX={8}>
            <Grid item xs={12}>
                
            </Grid>
            <Grid item sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', paddingBottom: 2, breakInside: 'avoid', breakAfter: 'auto' }} className='hideOnPrint' xs={12}>
                {filter}
                {print}
            </Grid>
            <Grid item xs={12}>
                {tabs}
            </Grid>
            <Grid item xs={12}>
                {panels}
            </Grid>
        </Grid>
    );
}